import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ADMIN_TOKEN_KEY = "elevatebuild_admin_token";

// Admin-scoped axios instance — separate from the trainee one so 401 handling
// redirects to /admin/login (not /portal/login).
export const adminApi = axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json" },
});

export function setAdminAuthToken(token) {
    if (token) {
        adminApi.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
        delete adminApi.defaults.headers.common["Authorization"];
    }
}

adminApi.interceptors.response.use(
    (r) => r,
    (err) => {
        const status = err?.response?.status;
        const url = err?.config?.url || "";
        const isLogin = url.includes("/admin/auth/login") || url.includes("/admin/auth/me");
        if ((status === 401 || status === 403) && !isLogin) {
            localStorage.removeItem(ADMIN_TOKEN_KEY);
            delete adminApi.defaults.headers.common["Authorization"];
            if (typeof window !== "undefined" && !window.location.pathname.startsWith("/admin/login")) {
                window.location.href = "/admin/login";
            }
        }
        return Promise.reject(err);
    },
);

export const adminAuth = {
    login: ({ username, password }) =>
        adminApi.post("/admin/auth/login", { username, password }).then((r) => r.data),
    me: () => adminApi.get("/admin/auth/me").then((r) => r.data),
};

export const adminEndpoints = {
    stats: () => adminApi.get("/admin/stats").then((r) => r.data),
    leads: () => adminApi.get("/admin/leads").then((r) => r.data),
    trainees: () => adminApi.get("/admin/trainees").then((r) => r.data),
    trainee: (id) => adminApi.get(`/admin/trainees/${id}`).then((r) => r.data),
    courses: () => adminApi.get("/admin/courses").then((r) => r.data),
    certificates: () => adminApi.get("/admin/certificates").then((r) => r.data),
    jobs: () => adminApi.get("/admin/jobs").then((r) => r.data),
    job: (id) => adminApi.get(`/admin/jobs/${id}`).then((r) => r.data),
    createJob: (payload) => adminApi.post("/admin/jobs", payload).then((r) => r.data),
    updateJob: (id, payload) => adminApi.put(`/admin/jobs/${id}`, payload).then((r) => r.data),
    deleteJob: (id) => adminApi.delete(`/admin/jobs/${id}`).then((r) => r.data),
    applications: (filters = {}) =>
        adminApi
            .get("/admin/applications", { params: filters })
            .then((r) => r.data),
    application: (id) => adminApi.get(`/admin/applications/${id}`).then((r) => r.data),
    applicationStatus: (id, status) =>
        adminApi.patch(`/admin/applications/${id}/status`, { status }).then((r) => r.data),
    // Downloads must retain the admin bearer, but the browser download flow
    // needs a blob → object-URL trick. Returns a Promise<{ blob, filename }>.
    downloadDocument: async (id, docType) => {
        const resp = await adminApi.get(
            `/admin/applications/${id}/documents/${docType}`,
            { responseType: "blob" },
        );
        const cd = resp.headers?.["content-disposition"] || "";
        const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
        const filename = match ? decodeURIComponent(match[1]) : `${docType}`;
        return { blob: resp.data, filename };
    },
};

export function triggerBlobDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 500);
}
